/**
 * "Moves due": the follow-ups the rhythm has put on the calendar. Today's
 * (and late ones) first, then what's coming up.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CalendarClock, ChevronDown, PartyPopper } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Avatar, Skeleton, useToast } from "@/components/ui";
import { Section } from "@/features/command/ui";
import { MoveItem } from "./MoveItem";
import { TONE_CLASS, dueOf, todayIso } from "./shared";
import type { Move, Rhythm } from "./types";

const UPCOMING_SHOWN = 4;

export function MovesPanel({ version, onChanged, rhythm, kind }: {
  version: number;
  /** Only this relationship (the tile filter on the page). */
  kind?: string | null;
  onChanged: () => void;
  rhythm?: Partial<Record<string, Rhythm>> | null;
}) {
  const toast = useToast();
  const [moves, setMoves] = useState<Move[] | null>(null);
  const [today, setToday] = useState(todayIso());
  const [showAll, setShowAll] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const { pathname, search } = useLocation();

  useEffect(() => {
    let live = true;
    api.get<{ moves: Move[]; today: string }>("/affiliates/moves")
      .then((d) => { if (!live) return; setMoves(d.moves ?? []); if (d.today) setToday(d.today); })
      .catch((err) => { if (live) { setMoves([]); toast.error(err instanceof ApiError ? err.message : "Couldn't load the moves"); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const mine = (moves ?? []).filter((m) => !kind || m.affiliate_kind === kind);
  const due = mine.filter((m) => m.due_date.slice(0, 10) <= today);
  const upcoming = mine.filter((m) => m.due_date.slice(0, 10) > today);
  const shownUpcoming = showAll ? upcoming : upcoming.slice(0, UPCOMING_SHOWN);

  const item = (m: Move, onCollapse?: () => void) => (
    <MoveItem
      key={m.id}
      move={m}
      person={{ id: m.affiliate_id, name: m.name ?? "Someone", kind: m.affiliate_kind }}
      rhythm={rhythm}
      onChanged={onChanged}
      onCollapse={onCollapse}
    />
  );

  return (
    <Section
      title="Moves due"
      icon={CalendarClock}
      count={due.length}
      action={moves && upcoming.length > 0 ? <span className="text-xs text-zinc-500">{upcoming.length} coming up</span> : undefined}
    >
      {moves == null ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          {due.length > 0 ? (
            <div className="space-y-2">{due.map((m) => item(m))}</div>
          ) : (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3.5 py-3">
              <PartyPopper className="h-4 w-4 shrink-0 text-emerald-300" />
              <div className="text-sm text-zinc-300">
                Nothing due today{kind ? " for this group" : ""}.{upcoming[0] ? ` Next up: ${upcoming[0].name} (${upcoming[0].label.toLowerCase()}), ${dueOf(upcoming[0].due_date).text}.` : " Add someone and their rhythm will start."}
              </div>
            </div>
          )}
          {upcoming.length > 0 && (
            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Coming up</div>
              <div className="overflow-hidden rounded-xl border border-zinc-800/70">
                {shownUpcoming.map((m, i) => (
                  openId === m.id ? (
                    <div key={m.id} className={cn("p-1.5", i > 0 && "border-t border-zinc-800/60")}>{item(m, () => setOpenId(null))}</div>
                  ) : (
                    <div key={m.id} className={cn("flex items-center gap-2.5 px-3 py-2 text-[13px]", i > 0 && "border-t border-zinc-800/60")}>
                      <Avatar name={m.name} size="xs" />
                      <Link
                        to={`/affiliates/${m.affiliate_id}`}
                        state={{ from: pathname + search }}
                        className="min-w-0 truncate font-medium text-zinc-200 hover:text-indigo-300"
                      >
                        {m.name}
                      </Link>
                      <span className="min-w-0 flex-1 truncate text-zinc-400">{m.label}</span>
                      <span className={cn("shrink-0 text-xs font-medium", TONE_CLASS[dueOf(m.due_date).tone])}>{dueOf(m.due_date).text}</span>
                      <button
                        type="button"
                        onClick={() => setOpenId(m.id)}
                        className="inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-1 text-xs font-medium text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100"
                        title="Do it now"
                      >
                        <span className="hidden sm:inline">Do it now</span>
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )
                ))}
              </div>
              {upcoming.length > UPCOMING_SHOWN && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="mt-2 text-xs font-medium text-indigo-300 hover:text-indigo-200"
                >
                  {showAll ? "Show fewer" : `Show all ${upcoming.length}`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
